import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";
import { Emblem } from "./Logo";

const EASE = [0.16, 1, 0.3, 1];

const Lines = ({ lines, lang }) => (
  <h1 className="hero-title font-display font-bold uppercase leading-[0.92] tracking-[-0.02em]" data-testid="hero-title">
    {lines.map((l, i) => (
      <span key={lang + i} className="block overflow-hidden pb-[0.06em]">
        <motion.span
          className={`block ${i === 1 ? "text-outline" : ""} ${i >= 2 ? "text-[var(--gold)]" : ""}`}
          initial={{ y: "110%", rotate: 4 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.25 + i * 0.14, ease: EASE }}
        >
          {l}
        </motion.span>
      </span>
    ))}
  </h1>
);

const HubOrb = ({ chip }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [16, -16]), { stiffness: 110, damping: 16 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-20, 20]), { stiffness: 110, damping: 16 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={reset}
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
      className="relative mx-auto w-[min(78vw,480px)] aspect-square [perspective:1200px]"
      data-testid="hero-hub-orb"
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative w-full h-full">
        <span className="signal-ring" style={{ animationDelay: "0s" }} />
        <span className="signal-ring" style={{ animationDelay: "1.3s" }} />
        <span className="signal-ring" style={{ animationDelay: "2.6s" }} />
        <div className="absolute inset-[-6%] rounded-full border border-[var(--gold)]/40 orbit" style={{ transform: "translateZ(-40px)" }}>
          <span className="absolute -top-1.5 left-1/2 w-3 h-3 rounded-full bg-[var(--gold)] shadow-[0_0_20px_var(--gold)]" />
        </div>
        <div className="absolute inset-0 rounded-full overflow-hidden bg-[radial-gradient(circle_at_35%_28%,#2b3550,#0A0D14_72%)] border border-[var(--gold)]/40 shadow-[0_40px_120px_-20px_rgba(229,169,60,0.45)] grid place-items-center" style={{ transform: "translateZ(0px)" }}>
          <img src="/assets/hub-device.webp" alt="QAZAQNET Hub" className="w-[68%] h-[68%] object-contain rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.55)] ring-1 ring-[var(--gold)]/50" data-testid="hero-hub-photo" />
        </div>
        <div className="absolute -left-2 md:-left-10 bottom-[12%] glass-chip" style={{ transform: "translateZ(90px)" }} data-testid="hero-chip">
          <span className="w-2 h-2 rounded-full bg-[var(--cyan)] animate-pulse" /> {chip}
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fgY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  return (
    <section id="top" ref={ref} className="relative isolate min-h-[100svh] overflow-hidden pt-28 md:pt-32 pb-10 flex flex-col" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img data-testid="hero-image" fetchPriority="high" decoding="async" src="/assets/hero-mangystau-v2.webp" alt="" className="w-full h-full object-cover opacity-75 kenburns kb-hero" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(10,13,20,0.5)_0%,rgba(10,13,20,0.6)_55%,#0A0D14_100%)]" />
      </motion.div>

      <motion.div style={{ y: fgY }} className="relative mx-auto max-w-[1400px] w-full px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center flex-1">
        <div className="lg:col-span-7">
          <motion.p
            key={lang + "eb"}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex flex-col gap-3 mb-8"
            data-testid="hero-eyebrow"
          >
            <span className="inline-flex self-start items-center gap-3 rounded-2xl md:rounded-full border border-[var(--gold)]/50 bg-[#0A0D14]/50 backdrop-blur-md pl-2 pr-4 py-1.5 font-display text-xs md:text-sm font-semibold tracking-[0.08em] text-[var(--text)]" data-testid="hero-company-name">
              <Emblem className="w-6 h-6 md:w-7 md:h-7 shrink-0" /> {t.hero.company}
            </span>
            <span className="text-[11px] md:text-xs uppercase tracking-[0.25em] text-[var(--gold)]">{t.hero.eyebrow}</span>
          </motion.p>
          <Lines lines={t.hero.lines} lang={lang} />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-[var(--text)]/80 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 1, ease: EASE }} className="mt-10 flex flex-wrap gap-4">
            <button data-testid="hero-cta-primary" onClick={() => scrollToId("contacts")} className="btn-gold">
              {t.hero.cta1} <ArrowUpRight className="w-4 h-4" />
            </button>
            <button data-testid="hero-cta-requisites" onClick={() => scrollToId("requisites")} className="btn-ghost">
              {t.hero.cta2}
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5">
          <HubOrb chip={t.hero.chip} />
        </div>
      </motion.div>

      <div className="relative mx-auto max-w-[1400px] w-full px-5 md:px-10 mt-14">
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/10" data-testid="hero-stats">
          {t.hero.stats.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 + i * 0.1 }}
              className="pt-6 pb-2 pr-4"
            >
              <div className="font-display text-2xl md:text-4xl text-[var(--text)]">
                {s.v} <span className="text-sm md:text-base text-[var(--gold)]">{s.u}</span>
              </div>
              <div className="text-xs md:text-sm text-[var(--muted)] mt-1">{s.l}</div>
            </motion.div>
          ))}
        </div>
        <button onClick={() => scrollToId("about")} data-testid="hero-scroll-button" className="hidden md:flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--muted)] mt-8 hover:text-[var(--gold)] transition-colors">
          <ArrowDown className="w-4 h-4 animate-bounce" /> {t.hero.scroll}
        </button>
      </div>
    </section>
  );
};
